import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Tab, Nav, Button, Form, Spinner, Badge, Accordion, Alert } from 'react-bootstrap';
import { colors } from '../../brand-colors';

const PLATFORM_BADGES = {
  x: { label: 'X', bg: '#000000', color: '#fff' },
  linkedin: { label: 'LinkedIn', bg: '#0077B5', color: '#fff' },
  youtube: { label: 'YouTube', bg: '#FF0000', color: '#fff' },
};

function IndustryPulse() {
  const [activeTab, setActiveTab] = useState('briefing');

  return (
    <Container className="py-4">
      <h1 className="mb-2" style={{
        fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
        letterSpacing: '0.15em',
      }}>
        Industry Pulse
      </h1>
      <p className="text-muted mb-4">
        Real-time intelligence on your market — what competitors are doing, what's trending, and where things are heading.
      </p>

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Nav variant="pills" className="mb-4" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'briefing', label: 'Intelligence Brief' },
            { key: 'trending', label: 'Trending Now' },
            { key: 'emerging', label: 'Emerging Signals' },
            { key: 'latest', label: 'Latest News' },
            { key: 'thought-leaders', label: 'Thought Leader Watch' },
            { key: 'competitor-watch', label: 'Competitor Watch' },
          ].map(tab => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                eventKey={tab.key}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.greyDarkest : 'transparent',
                  color: activeTab === tab.key ? colors.goldLight : colors.greyDark,
                  border: activeTab === tab.key ? 'none' : `1px solid ${colors.greyLight}`,
                  fontWeight: activeTab === tab.key ? 600 : 400,
                }}
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="briefing">
            <IntelligenceBrief />
          </Tab.Pane>
          <Tab.Pane eventKey="trending">
            <ZeitgeistFeed
              mode="trending"
              title="Trending Now"
              emptyMessage="No trending topics cached yet. Click Refresh to scan for what's buzzing in your industry, or generate content via Long Form / Short Form Posts to auto-populate."
              detailField="buzz_signal"
              detailLabel="Buzz"
            />
          </Tab.Pane>
          <Tab.Pane eventKey="emerging">
            <ZeitgeistFeed
              mode="emerging"
              title="Emerging Signals"
              emptyMessage="No emerging trends cached yet. Click Refresh to scan for early signals, or generate content via Long Form / Short Form Posts to auto-populate."
              detailField="trend_signal"
              detailLabel="Signal"
            />
          </Tab.Pane>
          <Tab.Pane eventKey="latest">
            <ZeitgeistFeed
              mode="latest"
              title="Latest News"
              emptyMessage="No recent news cached yet. Click Refresh to scan for the latest developments, or generate content via Long Form / Short Form Posts to auto-populate."
              detailField="development"
              detailLabel="Development"
            />
          </Tab.Pane>
          <Tab.Pane eventKey="thought-leaders">
            <ZeitgeistFeed
              mode="thought_leader"
              title="Thought Leader Watch"
              emptyMessage="No thought leader insights cached yet. Add thought leaders in Market Intelligence first, then click Refresh."
              detailField="their_take"
              detailLabel="Their Take"
              secondaryField="your_angle"
              secondaryLabel="Your Angle"
            />
          </Tab.Pane>
          <Tab.Pane eventKey="competitor-watch">
            <CompetitorWatch />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
}


function IntelligenceBrief() {
  const [briefing, setBriefing] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load cached briefing on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/industry-pulse/briefing', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.briefing) {
            setBriefing(data.briefing);
            setGeneratedAt(data.generated_at);
          }
        }
      } catch (err) {
        console.error('Failed to load cached briefing:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch('/api/industry-pulse/briefing', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.briefing) {
          setBriefing(data.briefing);
          setGeneratedAt(data.generated_at);
        } else {
          setError(data.message || 'Could not generate briefing.');
        }
      } else {
        const data = await res.json();
        setError(data.message || 'Briefing generation failed');
      }
    } catch (err) {
      setError('Network error during briefing generation');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>;
  }

  if (!briefing && !generating) {
    return (
      <Card className="text-center py-5" style={{ border: `1px solid ${colors.greyLight}` }}>
        <Card.Body>
          <h4 style={{ color: colors.greyDarkest, fontWeight: 600 }}>Intelligence Brief</h4>
          <p className="text-muted mb-3" style={{ maxWidth: 500, margin: '0 auto' }}>
            Synthesize all your intelligence tabs into one concise briefing — themes, signals worth watching, and questions to consider.
          </p>
          <p className="text-muted small mb-4">
            For best results, refresh a few tabs first so there's fresh data to analyze.
          </p>
          {error && <Alert variant="warning" className="mb-3">{error}</Alert>}
          <Button
            variant="dark"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <><Spinner size="sm" animation="border" className="me-1" /> Generating Briefing...</>
            ) : 'Generate Briefing'}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  if (generating) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="text-muted mt-3">Analyzing all signals and synthesizing your briefing... this takes 30-60 seconds.</p>
      </div>
    );
  }

  const CATEGORY_STYLES = {
    accelerant: { label: 'Accelerant', bg: '#1a7a3a', color: '#fff' },
    headwind: { label: 'Headwind', bg: '#a33a2a', color: '#fff' },
    watch: { label: 'Watch', bg: colors.goldDark, color: '#fff' },
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0" style={{ fontWeight: 600 }}>Intelligence Brief</h5>
          {generatedAt && (
            <small className="text-muted">Generated {new Date(generatedAt).toLocaleDateString()} at {new Date(generatedAt).toLocaleTimeString()}</small>
          )}
        </div>
        <Button variant="outline-dark" size="sm" onClick={handleGenerate} disabled={generating}>
          {generating ? <><Spinner size="sm" animation="border" className="me-1" /> Regenerating...</> : 'Regenerate'}
        </Button>
      </div>

      {/* BLUF */}
      {briefing.bluf && (
        <Card className="mb-4" style={{ backgroundColor: colors.greyDarkest, border: 'none' }}>
          <Card.Body style={{ padding: '1rem 1.2rem' }}>
            <p className="mb-0" style={{ color: colors.goldLight, fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.5 }}>
              {briefing.bluf}
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Position Summary — "What This Means For You" */}
      {briefing.position_summary && (
        <Card className="mb-4" style={{ borderLeft: `4px solid ${colors.goldDark}`, border: `1px solid ${colors.goldMid}` }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, padding: '0.6rem 1rem' }}>
            <strong>What This Means For Your Business</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-0" style={{ lineHeight: 1.6, fontSize: '1.05rem' }}>{briefing.position_summary}</p>
          </Card.Body>
        </Card>
      )}

      {/* Signal Classification */}
      {briefing.signals && briefing.signals.length > 0 && (
        <Card className="mb-4" style={{ border: `1px solid ${colors.greyLight}` }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, padding: '0.6rem 1rem' }}>
            <strong>Signal Classification</strong>
          </Card.Header>
          <Card.Body>
            {briefing.signals.map((sig, i) => {
              const catStyle = CATEGORY_STYLES[sig.category] || CATEGORY_STYLES.watch;
              return (
                <div key={i} className={i < briefing.signals.length - 1 ? 'mb-4 pb-3 border-bottom' : ''}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Badge style={{ backgroundColor: catStyle.bg, color: catStyle.color, minWidth: 85, textAlign: 'center' }}>
                      {catStyle.label}
                    </Badge>
                    <h6 className="mb-0" style={{ fontWeight: 700, color: colors.greyDarkest }}>{sig.name}</h6>
                  </div>
                  {sig.why && <p className="small mb-1">{sig.why}</p>}
                  {sig.action && (
                    <p className="small mb-1" style={{ color: '#1a7a3a' }}>
                      <strong>Potential action:</strong> {sig.action}
                    </p>
                  )}
                  {sig.trigger && sig.category === 'watch' && (
                    <p className="small mb-0" style={{ color: colors.goldDark }}>
                      <strong>Becomes actionable when:</strong> {sig.trigger}
                    </p>
                  )}
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}

      {/* Positioned to Take Advantage Of */}
      {briefing.asymmetric_edge && (
        <Card className="mb-4" style={{ borderLeft: `4px solid ${colors.goldDark}`, border: `1px solid ${colors.goldMid}` }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, padding: '0.6rem 1rem' }}>
            <strong>Positioned to Take Advantage Of</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-0" style={{ lineHeight: 1.6 }}>{briefing.asymmetric_edge}</p>
          </Card.Body>
        </Card>
      )}

      {/* Questions These Signals Raise */}
      {briefing.questions && briefing.questions.length > 0 && (
        <Card className="mb-4" style={{ border: `1px solid ${colors.greyLight}` }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, padding: '0.6rem 1rem' }}>
            <strong>Questions These Signals Raise</strong>
          </Card.Header>
          <Card.Body>
            {briefing.questions.map((q, i) => {
              const isObject = typeof q === 'object' && q !== null;
              return (
                <div key={i} className={i < briefing.questions.length - 1 ? 'mb-3 pb-3 border-bottom' : ''}>
                  <p className="mb-1" style={{ fontWeight: 600, lineHeight: 1.5 }}>
                    {isObject ? q.question : q}
                  </p>
                  {isObject && q.context && (
                    <p className="small text-muted mb-0">{q.context}</p>
                  )}
                </div>
              );
            })}
          </Card.Body>
        </Card>
      )}
    </>
  );
}


function ZeitgeistFeed({ mode, title, emptyMessage, detailField, detailLabel, secondaryField, secondaryLabel }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState(null);
  const [takeaways, setTakeaways] = useState([]);
  const [analyzingTakeaways, setAnalyzingTakeaways] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await fetch(`/api/industry-pulse/feed/${mode}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setFetchedAt(data.fetched_at);
        setIsStale(data.is_stale);
        if (data.takeaways && data.takeaways.length > 0) setTakeaways(data.takeaways);
      }
    } catch (err) {
      console.error(`Failed to fetch ${mode} feed:`, err);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await fetch(`/api/industry-pulse/feed/${mode}/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
        setFetchedAt(data.fetched_at);
        setIsStale(false);
        setTakeaways(data.takeaways || []);
        if (data.message) setError(data.message);
      } else {
        const data = await res.json();
        setError(data.error || 'Refresh failed');
      }
    } catch (err) {
      setError('Network error during refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzingTakeaways(true);
    try {
      const res = await fetch(`/api/industry-pulse/feed/${mode}/summary`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTakeaways(data.takeaways || []);
      }
    } catch (err) {
      console.error('Takeaway analysis error:', err);
    } finally {
      setAnalyzingTakeaways(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0" style={{ fontWeight: 600 }}>{title}</h5>
          {fetchedAt && (
            <small className="text-muted">
              Last updated: {new Date(fetchedAt).toLocaleString()}
              {isStale && <Badge bg="warning" text="dark" className="ms-2">Stale</Badge>}
            </small>
          )}
        </div>
        <div className="d-flex gap-2">
          {entries.length > 0 && (
            <Button
              variant="outline-dark"
              size="sm"
              onClick={handleAnalyze}
              disabled={analyzingTakeaways}
            >
              {analyzingTakeaways ? (
                <><Spinner size="sm" animation="border" className="me-1" /> Analyzing...</>
              ) : 'Regenerate Key Takeaways'}
            </Button>
          )}
          <Button
            variant="outline-dark"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <><Spinner size="sm" animation="border" className="me-1" /> Scanning...</>
            ) : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Key Takeaways card */}
      {takeaways.length > 0 && (
        <Card className="mb-4" style={{ border: `1px solid ${colors.goldMid}`, borderLeft: `4px solid ${colors.goldDark}` }}>
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, padding: '0.6rem 1rem' }}>
            <strong>Key Takeaways</strong>
          </Card.Header>
          <Card.Body style={{ padding: '1rem' }}>
            <ul className="mb-0" style={{ paddingLeft: '1.2rem' }}>
              {takeaways.map((t, i) => (
                <li key={i} className="mb-2" style={{ lineHeight: 1.5 }}>{t}</li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {error && <Alert variant="warning" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {refreshing && entries.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: colors.goldMid }} />
          <p className="text-muted mt-3">Scanning your industry with Grok... this takes 15-30 seconds.</p>
        </div>
      ) : entries.length === 0 ? (
        <Alert variant="info" style={{ backgroundColor: '#1E3A5F', color: '#fff', border: 'none' }}>
          {emptyMessage}
        </Alert>
      ) : (
        <div className="list-group">
          {entries.map(entry => (
            <div key={entry.id} className="list-group-item px-3 py-3" style={{ borderLeft: `3px solid ${colors.goldMid}` }}>
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="mb-1" style={{ fontWeight: 600 }}>
                    {entry.url ? (
                      <a
                        href={entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: colors.greyDarkest, textDecoration: 'none' }}
                        onMouseEnter={e => { e.target.style.color = colors.goldDark; e.target.style.textDecoration = 'underline'; }}
                        onMouseLeave={e => { e.target.style.color = colors.greyDarkest; e.target.style.textDecoration = 'none'; }}
                      >
                        {entry.headline}
                      </a>
                    ) : entry.headline}
                  </h6>

                  {/* Primary detail field (buzz_signal, trend_signal, development, their_take) */}
                  {entry[detailField] && (
                    <p className="mb-1 small">
                      <strong>{detailLabel}:</strong> {entry[detailField]}
                    </p>
                  )}

                  {/* Secondary detail field (your_angle for thought leaders) */}
                  {secondaryField && entry[secondaryField] && (
                    <p className="mb-1 small" style={{ color: colors.goldDark }}>
                      <strong>{secondaryLabel}:</strong> {entry[secondaryField]}
                    </p>
                  )}

                  {/* Why it matters */}
                  {entry.why_it_matters && !entry[detailField]?.includes(entry.why_it_matters) && (
                    <p className="mb-1 small text-muted">
                      {entry.why_it_matters}
                    </p>
                  )}

                  {/* Thought leader name */}
                  {entry.thought_leader && mode === 'thought_leader' && (
                    <Badge bg="dark" className="me-1">{entry.thought_leader}</Badge>
                  )}

                  {/* Score badge */}
                  {entry.score?.overall && (
                    <Badge
                      style={{
                        backgroundColor: entry.score.overall >= 4 ? colors.goldDark : colors.greyMid,
                        color: '#fff',
                      }}
                    >
                      Score: {entry.score.overall.toFixed(1)}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}


function CompetitorWatch() {
  const [competitors, setCompetitors] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [discoveringId, setDiscoveringId] = useState(null);
  const [editForms, setEditForms] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchCompetitors = useCallback(async () => {
    try {
      const res = await fetch('/api/industry-pulse/competitors', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCompetitors(data.competitors || []);
        // Initialize edit forms
        const forms = {};
        (data.competitors || []).forEach(c => {
          forms[c.id] = {
            x_handle: c.x_handle || '',
            linkedin_url: c.linkedin_url || '',
            youtube_channel_url: c.youtube_channel_url || '',
          };
        });
        setEditForms(forms);
      }
    } catch (err) {
      console.error('Failed to fetch competitors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const res = await fetch('/api/industry-pulse/competitor-feed', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFeed(data.feed || []);
      }
    } catch (err) {
      console.error('Failed to fetch feed:', err);
    } finally {
      setFeedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompetitors().then(() => fetchFeed());
  }, [fetchCompetitors, fetchFeed]);

  const handleFormChange = (compId, field, value) => {
    setEditForms(prev => ({
      ...prev,
      [compId]: { ...prev[compId], [field]: value }
    }));
  };

  const handleSave = async (compId) => {
    setSavingId(compId);
    try {
      const res = await fetch(`/api/industry-pulse/competitors/${compId}/socials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForms[compId]),
      });
      if (res.ok) {
        // Update just this competitor in state without wiping other unsaved forms
        setCompetitors(prev => prev.map(c =>
          c.id === compId ? { ...c, ...editForms[compId] } : c
        ));
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSavingId(null);
    }
  };

  const handleDiscover = async (compId) => {
    setDiscoveringId(compId);
    try {
      const res = await fetch(`/api/industry-pulse/competitors/${compId}/discover-socials`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const socials = data.socials || {};
        setEditForms(prev => ({
          ...prev,
          [compId]: {
            x_handle: socials.x_handle || prev[compId]?.x_handle || '',
            linkedin_url: socials.linkedin_url || prev[compId]?.linkedin_url || '',
            youtube_channel_url: socials.youtube_channel_url || prev[compId]?.youtube_channel_url || '',
          }
        }));
      }
    } catch (err) {
      console.error('Discover error:', err);
    } finally {
      setDiscoveringId(null);
    }
  };

  const handleRefresh = async (compId) => {
    setRefreshingId(compId);
    try {
      const res = await fetch(`/api/industry-pulse/competitors/${compId}/fetch-posts`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchFeed();
      }
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleRefreshAll = async () => {
    setFeedLoading(true);
    for (const comp of competitors) {
      const hasSocials = comp.x_handle || comp.linkedin_url || comp.youtube_channel_url;
      if (!hasSocials) continue;
      try {
        await fetch(`/api/industry-pulse/competitors/${comp.id}/fetch-posts`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (err) {
        console.error(`Refresh error for ${comp.competitor_name}:`, err);
      }
    }
    await fetchFeed();
    setFeedLoading(false);
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>;
  }

  if (competitors.length === 0) {
    return (
      <Alert variant="warning">
        No competitors found. Add competitors in <strong>Products &gt; Competitive Analysis</strong> first, then return here to track their social activity.
      </Alert>
    );
  }

  const competitorsWithSocials = competitors.filter(c => c.x_handle || c.linkedin_url || c.youtube_channel_url);

  return (
    <>
      {/* Settings Accordion */}
      <Accordion className="mb-4">
        <Accordion.Item eventKey="0" style={{ border: `1px solid ${colors.greyLight}` }}>
          <Accordion.Header>
            <span style={{ fontWeight: 600 }}>Competitor Social Profiles ({competitors.length})</span>
          </Accordion.Header>
          <Accordion.Body>
            {competitors.map(comp => (
              <Card key={comp.id} className="mb-3" style={{ border: `1px solid ${colors.greyLight}` }}>
                <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontWeight: 600 }}>{comp.competitor_name}</span>
                    {comp.competitor_url && (
                      <small style={{ color: colors.greyLight }}>{comp.competitor_url}</small>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold">X Handle</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="@handle"
                          value={editForms[comp.id]?.x_handle || ''}
                          onChange={e => handleFormChange(comp.id, 'x_handle', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold">LinkedIn URL</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="https://linkedin.com/company/..."
                          value={editForms[comp.id]?.linkedin_url || ''}
                          onChange={e => handleFormChange(comp.id, 'linkedin_url', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold">YouTube Channel</Form.Label>
                        <Form.Control
                          size="sm"
                          placeholder="https://youtube.com/@..."
                          value={editForms[comp.id]?.youtube_channel_url || ''}
                          onChange={e => handleFormChange(comp.id, 'youtube_channel_url', e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline-dark"
                      onClick={() => handleDiscover(comp.id)}
                      disabled={discoveringId === comp.id || !comp.competitor_url}
                    >
                      {discoveringId === comp.id ? (
                        <><Spinner size="sm" animation="border" className="me-1" /> Scanning...</>
                      ) : 'Auto-Discover'}
                    </Button>
                    <Button
                      size="sm"
                      variant="dark"
                      onClick={() => handleSave(comp.id)}
                      disabled={savingId === comp.id}
                    >
                      {savingId === comp.id ? (
                        <><Spinner size="sm" animation="border" className="me-1" /> Saving...</>
                      ) : 'Save'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* Feed Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0" style={{ fontWeight: 600 }}>Competitor Feed</h5>
        <Button
          variant="outline-dark"
          size="sm"
          onClick={handleRefreshAll}
          disabled={feedLoading || competitorsWithSocials.length === 0}
        >
          {feedLoading ? (
            <><Spinner size="sm" animation="border" className="me-1" /> Refreshing...</>
          ) : 'Refresh All'}
        </Button>
      </div>

      {feedLoading && feed.length === 0 ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.goldMid }} /></div>
      ) : feed.length === 0 ? (
        <Alert variant="info" style={{ backgroundColor: '#1E3A5F', color: '#fff', border: 'none' }}>
          No posts cached yet. Configure social profiles above, then click <strong>Refresh All</strong> to fetch recent competitor activity.
        </Alert>
      ) : (
        feed.map(group => (
          <Card key={group.source_id} className="mb-4" style={{ border: `1px solid ${colors.greyLight}` }}>
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}
              className="d-flex justify-content-between align-items-center"
            >
              <span style={{ fontWeight: 600 }}>{group.competitor_name}</span>
              <div className="d-flex align-items-center gap-2">
                {group.fetched_at && (
                  <small style={{ color: colors.greyLight }}>
                    Updated {new Date(group.fetched_at).toLocaleDateString()}
                  </small>
                )}
                <Button
                  size="sm"
                  variant="outline-light"
                  onClick={() => handleRefresh(group.source_id)}
                  disabled={refreshingId === group.source_id}
                  style={{ borderColor: colors.goldLight, color: colors.goldLight }}
                >
                  {refreshingId === group.source_id ? (
                    <Spinner size="sm" animation="border" />
                  ) : 'Refresh'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {group.posts.length === 0 ? (
                <p className="text-muted p-3 mb-0">No recent posts found.</p>
              ) : (
                <div className="list-group list-group-flush">
                  {group.posts.map((post, idx) => (
                    <div key={idx} className="list-group-item d-flex align-items-start gap-3 px-3 py-2">
                      <PlatformBadge platform={post.platform} />
                      <div className="flex-grow-1">
                        {post.url ? (
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: colors.greyDarkest, textDecoration: 'none', fontWeight: 500 }}
                            onMouseEnter={e => e.target.style.color = colors.goldDark}
                            onMouseLeave={e => e.target.style.color = colors.greyDarkest}
                          >
                            {post.headline || 'Untitled post'}
                          </a>
                        ) : (
                          <span style={{ fontWeight: 500 }}>{post.headline || 'Untitled post'}</span>
                        )}
                        {post.published_at && (
                          <small className="text-muted d-block mt-1">
                            {new Date(post.published_at).toLocaleDateString()}
                          </small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        ))
      )}
    </>
  );
}


function PlatformBadge({ platform }) {
  const config = PLATFORM_BADGES[platform] || { label: platform || '?', bg: colors.greyMid, color: '#fff' };
  return (
    <Badge
      style={{
        backgroundColor: config.bg,
        color: config.color,
        minWidth: '70px',
        textAlign: 'center',
        marginTop: '2px',
      }}
    >
      {config.label}
    </Badge>
  );
}

export default IndustryPulse;
